import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {ANCHOR_BOTTOM, ANCHOR_RIGHT} from "@khan/guide-core";
import {RequestInfo} from "@khan/shared-core";
import {useMediaQuery} from "@khan/util-media-query";

import type {KhanmigoChatUIProps} from "../guide-chat-window/guide-chat-window-state.tsx";
import {GuideChatWindow} from "../guide-chat-window/guide-chat-window.tsx";
import {HEADER_HEIGHT} from "../header/constants.ts";

import {GuideSidebarNotifications} from "./notifications/guide-sidebar-notifications.tsx";
import {GuideSidebarOpener} from "./opener/guide-sidebar-opener.tsx";
import {SkipToKhanmigoLink} from "./opener/skip-link.tsx";

const SIDEBAR_MIN_WIDTH = 400;

export const KhanmigoSidebar = ({
    swagId,
    mediaSize,
    open,
    expanded,
    isGuideOffline,
    isCoeditorOpen,
    showMenu,
    showPreferences,
    shouldHideToggleAndNotifications,
    onEscapeKey,
    toggleChatWindow,
    toggleExpanded,
    toggleMenu,
    togglePreferences,
}: KhanmigoChatUIProps) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const isExtraLargeScreen = useMediaQuery("(min-width: 1200px)");

    // In a mobile embedded situation, don't show the Khanmigo sidebar for now
    if (RequestInfo.isMobileApp) {
        return null;
    }

    return (
        <>
            <View
                // This is a workaround to be able to use inert attribute in
                // React. We should be able to remove this when we upgrade to
                // React 19.
                {...{inert: open ? undefined : ""}}
                aria-hidden={!open ? "true" : undefined}
                testId="khanmigo-sidebar"
                onKeyDown={onEscapeKey}
                style={[
                    styles.chatUIWrapper,
                    open && styles.chatUIWrapperOpen,
                    isExtraLargeScreen &&
                        expanded &&
                        !isCoeditorOpen &&
                        styles.expandedChatUI,
                    isCoeditorOpen && styles.coeditorOpen,
                    !open && styles.chatUIClosed,
                    prefersReducedMotion && styles.removeTransition,
                ]}
            >
                {/* The chat window is mounted even when closed so that the
                contents of the chat input are preserved when the sidebar is
                reopened. */}
                <GuideChatWindow
                    mediaSize={mediaSize}
                    isCoeditorOpen={isCoeditorOpen}
                    onClose={toggleChatWindow}
                    showMenu={showMenu}
                    onToggleMenu={toggleMenu}
                    expanded={expanded}
                    onToggleExpanded={toggleExpanded}
                    showPreferences={showPreferences}
                    onTogglePreferences={togglePreferences}
                />
            </View>

            {!shouldHideToggleAndNotifications && (
                <GuideSidebarNotifications
                    style={[
                        styles.toggleContainer,
                        open && styles.toggleContainerHidden,
                    ]}
                >
                    <View
                        aria-hidden={open ? "true" : undefined}
                        style={[
                            styles.openerWrapper,
                            open && styles.openerWrapperHidden,
                            prefersReducedMotion && styles.removeTransition,
                        ]}
                    >
                        <GuideSidebarOpener
                            isGuideOffline={isGuideOffline}
                            onClick={toggleChatWindow}
                            swagId={swagId}
                        />
                    </View>
                </GuideSidebarNotifications>
            )}
            <SkipToKhanmigoLink onToggle={toggleChatWindow} />
        </>
    );
};

const styles = StyleSheet.create({
    chatUIWrapper: {
        flexDirection: "column",
        display: "grid",
        gridTemplateAreas: `"header header" "coeditor chat"`,
        gridTemplateRows: `${HEADER_HEIGHT}px 1fr`,
        gridTemplateColumns: `0 ${SIDEBAR_MIN_WIDTH}px`,
        overflow: "hidden",
        backgroundColor: color.offWhite,
    },
    chatUIWrapperOpen: {
        width: `calc(min(30vw, ${SIDEBAR_MIN_WIDTH}px))`,
        minWidth: SIDEBAR_MIN_WIDTH,
        borderLeft: `1px solid ${color.offBlack16}`,
        transition: "all 0.5s ease-in-out",
        "@media screen and (max-width: 719px)": {
            display: "flex",
            width: "100vw",
            height: "100%",
            position: "absolute" as any,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transition: "none",
        },
    } as any,
    chatUIClosed: {
        minWidth: 0,
        width: 0,
        border: "none",
        transition: "all 0.5s ease-in-out",
        "@media screen and (max-width: 719px)": {
            transition: "none",
        },
    } as any,
    removeTransition: {
        transition: "none",
    },
    expandedChatUI: {
        gridTemplateColumns: `0 calc(minmax(50vw, ${SIDEBAR_MIN_WIDTH}px))`,
        width: `calc(minmax(50vw, ${SIDEBAR_MIN_WIDTH}px))`,
        maxWidth: "50vw",
        minInlineSize: SIDEBAR_MIN_WIDTH,
    },
    coeditorOpen: {
        gridTemplateColumns: `auto ${SIDEBAR_MIN_WIDTH}px`,
        width: "unset",
    },
    openerWrapper: {
        opacity: 1,
    },
    openerWrapperHidden: {
        opacity: 0,
        pointerEvents: "none",
    },
    toggleContainer: {
        position: "fixed",
        bottom: ANCHOR_BOTTOM,
        insetInlineEnd: ANCHOR_RIGHT,
        flexDirection: "row",
        justifyContent: "flex-end",
        // Apply the fade immediately, but delay the transform so that the
        // button doesn't disappear before the fade is complete.
        transition: "opacity 0.3s ease-in-out, transform 0s 0.3s ease-in-out",
    },
    toggleContainerHidden: {
        // Move the button off-screen so that it doesn't interfere with
        // controls in the chat UI.
        transform: "translateY(200px)",
    },
});
